import {Component, computed, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {forkJoin} from 'rxjs';
import {CompanyService} from '@services/company.service';
import {ICompanyDetailsSimplified} from '@models/company.interface';
import {ICompanyUserFile} from '@models/workspace.interface';
import {NotificationService} from '@services/frontend/notification.service';
import {ModalService} from '@services/frontend/modal.service';
import {DocumentDeleteDialog} from '@shared/components/document-delete-dialog/document-delete-dialog';
import {httpErrorMessage} from 'app/utils/http-error.utils';
import {formatFileSize} from 'app/utils/file-size.utils';

interface ExpertDocumentRow {
  company: ICompanyDetailsSimplified;
  file: ICompanyUserFile;
}

@Component({
  selector: 'app-expert-documents',
  imports: [DatePipe],
  templateUrl: './expert-documents.html',
})
export class ExpertDocuments implements OnInit {
  private readonly companyService = inject(CompanyService);
  private readonly notificationService = inject(NotificationService);
  private readonly modalService = inject(ModalService);

  loading = signal(true);
  uploading = signal(false);
  downloadingId = signal<string | null>(null);
  deleting = signal(false);
  companies: WritableSignal<ICompanyDetailsSimplified[]> = signal([]);
  rows: WritableSignal<ExpertDocumentRow[]> = signal([]);

  protected readonly formatSize = formatFileSize;

  busy = computed(() =>
    this.loading() || this.uploading() || this.downloadingId() !== null || this.deleting()
  );

  busyMessage = computed(() => {
    if (this.uploading()) {
      return 'Envoi du fichier en cours…';
    }
    if (this.downloadingId()) {
      return 'Téléchargement en cours…';
    }
    if (this.deleting()) {
      return 'Suppression en cours…';
    }
    return 'Chargement des documents…';
  });

  ngOnInit(): void {
    this.loading.set(true);
    this.companyService.getCompanies().subscribe({
      next: (companies) => {
        this.companies.set(companies);
        if (!companies.length) {
          this.rows.set([]);
          this.loading.set(false);
          return;
        }
        forkJoin(companies.map((company) => this.companyService.listUserFiles(company.siret, 'GENERIC'))).subscribe({
          next: (results) => {
            const rows = companies.flatMap((company, index) =>
              (results[index] || []).map((file) => ({company, file}))
            );
            this.rows.set(rows);
            this.loading.set(false);
          },
          error: () => {
            this.rows.set([]);
            this.loading.set(false);
          },
        });
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      },
    });
  }

  download(row: ExpertDocumentRow): void {
    if (this.busy()) {
      return;
    }
    this.downloadingId.set(row.file.id);
    this.companyService.downloadUserFile(row.company.siret, row.file.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = row.file.originalFilename;
        a.click();
        window.URL.revokeObjectURL(url);
        this.downloadingId.set(null);
      },
      error: () => {
        this.downloadingId.set(null);
        this.notificationService.error('Téléchargement impossible', 3000);
      },
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.busy()) {
      return;
    }
    const firstCompany = this.companies()[0];
    if (!firstCompany) {
      this.notificationService.error('Ajoutez un client avant de déposer un document', 3000);
      return;
    }
    this.uploading.set(true);
    this.companyService.uploadUserFile(firstCompany.siret, file, 'GENERIC').subscribe({
      next: () => {
        this.notificationService.success('Document uploadé', 2000);
        this.uploading.set(false);
        this.ngOnInit();
      },
      error: (err) => {
        this.notificationService.error(httpErrorMessage(err, 'Erreur lors de l’upload'), 5000);
        this.uploading.set(false);
      },
    });
  }

  delete(row: ExpertDocumentRow): void {
    if (this.busy()) {
      return;
    }
    const ref = this.modalService.open(DocumentDeleteDialog, {
      title: 'Confirmation',
      data: {fileName: row.file.originalFilename},
    });

    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.deleting.set(true);
      this.companyService.deleteUserFile(row.company.siret, row.file.id).subscribe({
        next: () => {
          this.deleting.set(false);
          this.notificationService.success('Document supprimé', 2000);
          this.rows.update((current) => current.filter((item) => item.file.id !== row.file.id));
        },
        error: (err) => {
          this.deleting.set(false);
          this.notificationService.error(httpErrorMessage(err, 'Erreur lors de la suppression'), 5000);
        },
      });
    });
  }

  fileEmoji(contentType: string | null): string {
    if (contentType?.startsWith('application/pdf')) return '📄';
    if (contentType?.startsWith('image/')) return '🖼️';
    if (contentType?.includes('spreadsheet') || contentType?.includes('excel')) return '📊';
    if (contentType?.includes('word') || contentType?.includes('document')) return '📝';
    return '📎';
  }
}
